import { Button, Form, Input, Modal, Select, message } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
	getCurrency,
	getObjectValues,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';
import { CURRENCIES, ROLE_TYPES } from '../../utils/DataConstants';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	contractorClientsRestCrudThunksForBuyer,
	contractorClientsRestCrudThunksForSupplier,
} from '../../thunks/contractor_clients_thunks';
import { capitalize } from '../../utils/DataFormatterUtils';

const FORM_NAME = 'contractor-client-form';

const AddOrEditContractorClientModal: FC<any> = ({
	client,
	onCancel,
	create,
	update,
	targetCollectionName,
}) => {
	const [saving, setSaving] = useState(false);

	const isUpdate = useMemo(() => nullSafeGet('id', client), [client]);

	const submitMethod = useMemo(() => (isUpdate ? update : create), [create, isUpdate, update]);

	const initialValues = useMemo(
		() => ({
			...(client || {}),
			isBuyer: client ? `${client.isBuyer}` : 'true',
			isSupplier: client ? `${client.isSupplier}` : 'false',
		}),
		[client]
	);

	const onSubmit = useCallback(
		(values) => {
			const entity = {
				...(client || {}),
				...values,
				isBuyer: values.isBuyer === 'true',
				isSupplier: values.isBuyer !== 'true',
			};

			setSaving(true);
			submitMethod(entity, 'id', targetCollectionName)
				.then(() => {
					message.success(`Client ${isUpdate ? 'saved' : 'added'} successfully!`);
					onCancel();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[client, isUpdate, onCancel, submitMethod, targetCollectionName]
	);

	const currencyRecordsList = getObjectValues(CURRENCIES);

	return (
		<Modal
			visible={true}
			width={600}
			title={`${isUpdate ? 'Edit' : 'New'} Client`}
			onCancel={onCancel}
			closable
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{isUpdate ? 'Save' : 'Add'}
				</Button>,
			]}
		>
			<Form
				id={FORM_NAME}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				<Form.Item
					name="isBuyer"
					rules={[
						{
							required: true,
							message: 'Please select a valid option.',
						},
					]}
					label="Is your client, customer facing or service provider?"
				>
					<OWRadioGroup
						valueAccessor={(item) => item.value}
						renderRecord={(item) => item.label}
						items={[
							{
								label: 'Customer Facing',
								value: 'true',
							},
							{
								label: 'Service Provider',
								value: 'false',
							},
						]}
					/>
				</Form.Item>
				<Form.Item
					name="name"
					label="Client Name"
					rules={[
						{
							required: true,
							message: 'Please give your client a name.',
						},
					]}
				>
					<Input style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item
					name="currencyId"
					label="Currency"
					rules={[{ required: true, message: 'Please select a currency for this event.' }]}
				>
					<Select
						showSearch={true}
						optionFilterProp="children"
						filterOption={(input, option) =>
							currencyRecordsList[option.props.value].displayNamePlural
								.toLowerCase()
								.indexOf(input.toLowerCase()) >= 0
						}
						placeholder="Currency"
					>
						{currencyRecordsList.map((currency) => (
							<Select.Option key={currency.id} value={currency.id}>
								{capitalize(currency.displayNamePlural)} ({currency.id})
							</Select.Option>
						))}
					</Select>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	create: (entity, primaryKey, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.create(
						entity,
						primaryKey,
						targetCollectionName
				  )
				: contractorClientsRestCrudThunksForBuyer.create(entity, primaryKey, targetCollectionName)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? contractorClientsRestCrudThunksForSupplier.update(entity)
				: contractorClientsRestCrudThunksForBuyer.update(entity)
		),
});

const ComponentWithoutUserType = connect(null, mapDispatchToProps)(AddOrEditContractorClientModal);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
